import type { ConnectionStatus, AgentMode } from "@microsoft/voice-widget-core-client";
import type { WidgetShellOptions, WidgetViewState } from "./types.js";

export type DisplayState =
  | "idle"
  | "connecting"
  | "listening"
  | "thinking"
  | "speaking"
  | "connected"
  | "disconnected"
  | "error";

export function deriveDisplayState(status: ConnectionStatus, mode: AgentMode | null): DisplayState {
  switch (status) {
    case "connected":
      return mode ?? "connected";
    case "idle":
    case "connecting":
    case "disconnected":
    case "error":
      return status;
    default: {
      // Exhaustiveness guard: if ConnectionStatus gains a member, this fails to compile.
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

export type StringKey =
  | DisplayState
  | "startCall"
  | "endCall"
  | "expandAssistant"
  | "minimize"
  | "mute"
  | "unmute"
  | "panelLabel"
  | "transcriptLabel"
  | "userLabel"
  | "agentLabel"
  | "textInputPlaceholder"
  | "sendMessage";

export type Strings = Record<StringKey, string>;

export type Direction = "ltr" | "rtl" | "auto";

export function resolveDir(value: unknown): Direction | undefined {
  return value === "ltr" || value === "rtl" || value === "auto" ? value : undefined;
}

export const DEFAULT_STRINGS: Strings = {
  idle: "Talk to assistant",
  connecting: "Connecting…",
  listening: "Listening…",
  thinking: "Thinking…",
  speaking: "Speaking…",
  connected: "Connected",
  disconnected: "Disconnected",
  error: "Something went wrong",
  startCall: "Start a call",
  endCall: "End call",
  expandAssistant: "Expand assistant",
  minimize: "Minimize",
  mute: "Mute",
  unmute: "Unmute",
  panelLabel: "Voice assistant",
  transcriptLabel: "Conversation transcript",
  userLabel: "You",
  agentLabel: "Assistant",
  textInputPlaceholder: "Type a message",
  sendMessage: "Send message",
};

export function resolveStrings(opts: {
  startLabel?: string;
  langStrings?: Partial<Strings>;
}): Strings {
  const merged: Strings = { ...DEFAULT_STRINGS };
  if (opts.langStrings) {
    for (const key of Object.keys(DEFAULT_STRINGS) as StringKey[]) {
      const v = opts.langStrings[key];
      if (typeof v === "string" && v.trim().length > 0) merged[key] = v;
    }
  }
  if (typeof opts.startLabel === "string" && opts.startLabel.trim()) {
    merged.idle = opts.startLabel; // back-compat: wins over langStrings.idle
  }
  return merged;
}

export function createInitialState(opts: WidgetShellOptions): WidgetViewState {
  const variant = opts.variant ?? "compact";
  const placement = opts.placement ?? "bottom-right";
  const expanded = variant === "bar" ? true : (opts.defaultExpanded ?? false);
  return {
    status: "idle",
    mode: null,
    expanded,
    variant,
    placement,
    muted: false,
  };
}
